import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export function RestTimer({
  seconds,
  onDismiss,
  bottomOffset = 'var(--mobile-nav-clearance)',
}: {
  seconds: number;
  onDismiss: () => void;
  bottomOffset?: string;
}) {
  const [left, setLeft] = useState(seconds);

  useEffect(() => {
    setLeft(seconds);
  }, [seconds]);

  useEffect(() => {
    if (left <= 0) {
      onDismiss();
      return;
    }
    const t = window.setInterval(() => setLeft((n) => n - 1), 1000);
    return () => clearInterval(t);
  }, [left, onDismiss]);

  const pct = seconds > 0 ? (left / seconds) * 100 : 0;
  const m = Math.floor(left / 60);
  const s = left % 60;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: 12 }}
      className="fixed right-4 z-[60] flex flex-col items-center"
      style={{ bottom: bottomOffset }}
    >
      <button
        type="button"
        onClick={onDismiss}
        className="relative flex h-[88px] w-[88px] items-center justify-center rounded-full border border-sky-400/30 bg-[#0d1528]/95 shadow-[0_8px_32px_rgba(0,163,255,0.25)] backdrop-blur-xl"
        aria-label="Rest timer"
      >
        <svg className="absolute inset-0 -rotate-90" viewBox="0 0 88 88">
          <circle cx="44" cy="44" r="38" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
          <circle
            cx="44"
            cy="44"
            r="38"
            fill="none"
            stroke="url(#restGrad)"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 38}
            strokeDashoffset={2 * Math.PI * 38 * (1 - pct / 100)}
          />
          <defs>
            <linearGradient id="restGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#00A3FF" />
              <stop offset="100%" stopColor="#0066FF" />
            </linearGradient>
          </defs>
        </svg>
        <span className="font-display text-lg font-bold tabular-nums text-white">
          {m}:{s.toString().padStart(2, '0')}
        </span>
      </button>
      <span className="mt-1.5 text-[10px] font-semibold uppercase tracking-wide text-sky-300/80">Rest</span>
    </motion.div>
  );
}

export function RestTimerHost({
  active,
  seconds,
  onDismiss,
  bottomOffset,
}: {
  active: boolean;
  seconds: number;
  onDismiss: () => void;
  bottomOffset?: string;
}) {
  return (
    <AnimatePresence>
      {active ? (
        <RestTimer key={seconds} seconds={seconds} onDismiss={onDismiss} bottomOffset={bottomOffset} />
      ) : null}
    </AnimatePresence>
  );
}
